import { useEffect, ReactElement } from 'react';
import CheckIcon from '@guildeducationinc/recess/icons/CheckIcon';
import HighlightOffIcon from '@guildeducationinc/recess/icons/HighlightOffIcon';
import { DialogButton, TextLink, Typography, recessColors } from '@guildeducationinc/recess';
import { useMediaQuery } from 'react-responsive';
import { ModalOptions, useModal } from '@src/context/ModalContext';
import { Application } from '@src/hooks/useApplications';
import { MOBILE_QUERY, SALESFORCE_NEW_REQUEST_URL } from '@src/constants';
import { clickable } from '@src/analytics';

interface EnrollmentConfirmationModalProps {
  application: Application | undefined;
  enrollmentIntent: boolean;
}

const EnrollmentSuccessModal = ({
  application,
  enrollmentIntent,
}: EnrollmentConfirmationModalProps): ReactElement => {
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const { setModalOptions, closeModal } = useModal();
  const apName = application?.program?.studentFacingAcademicPartner?.name;
  const term = application?.enrollmentIntentions?.[0]?.term;
  const termName = term?.name;

  const logo = application?.program?.studentFacingAcademicPartner?.logos?.color;

  const icon =
    enrollmentIntent === true ? (
      <CheckIcon color={recessColors.green.base} />
    ) : (
      <HighlightOffIcon color={recessColors.red.base} />
    );

  const supportClickable = () => {
    clickable({
      loggedIn: true,
      product: 'home',
      tag: 'Enrollment_success',
      text: 'contact Guild support',
      type: 'link',
    });
  };

  const primaryButtonConfig: DialogButton = {
    onClick: () => {
      closeModal();
    },
    target: '_blank',
    text: 'Close',
  };

  const enrollmentConfirmationTitle = 'Thank you for confirming!';
  const enrollmentConfirmationSubtitle = `${termName} ${apName} enrollment confirmed`;
  const enrollmentDeclineSubtitle = `${termName} ${apName} enrollment declined`;
  const supportLink = (
    <TextLink
      testid='support-link'
      label='Contact Guild Support'
      onClick={() => supportClickable()}
      target='_blank'
      href={SALESFORCE_NEW_REQUEST_URL}
    >
      contact Guild Support
    </TextLink>
  );
  const enrollmentConfirmationOptions: ModalOptions = {
    primaryButton: primaryButtonConfig,
    closable: true,
    describedBy: 'enrollment-confirmation-description',
    labelledBy: 'enrollment-confirmation-title',
    tag: 'enrollment_confirmation',
    titleComponent: 'h2',
    titleSize: 'h2',
  };

  useEffect(() => {
    setModalOptions(enrollmentConfirmationOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrollmentIntent]);

  return (
    <div className='enrollmentModal'>
      <div className='enrollmentModalHeader enrollmentSuccessModalHeader'>
        <Typography
          className='enrollmentConfirmationTitle enrollmentSuccessModalSubtitleTitle'
          id='enrollment-confirmation-title'
          variant='h2'
          gutterBottom={isMobile ? 'sm' : undefined}
        >
          {enrollmentConfirmationTitle}
        </Typography>
        {logo && <img className='enrollmentModalLogo' alt={`${apName} logo`} src={logo} />}
      </div>
      <div className='enrollmentModalSubtitleIcon'>
        {icon}
        <Typography
          className='enrollmentModalSubtitleText'
          id='enrollment-confirmation-subtitle'
          variant='h3'
          gutterBottom='sm'
        >
          {enrollmentIntent ? enrollmentConfirmationSubtitle : enrollmentDeclineSubtitle}
        </Typography>
      </div>
      <div>
        {enrollmentIntent ? (
          <Typography gutterTop='lg'>
            {`You accepted your spot in ${apName}'s ${
              application?.program?.name
            } on ${new Date().toLocaleDateString()}. You'll receive an email before your start date with instructions for starting your program. In the meantime, review next steps below or `}
            {supportLink} with questions.
          </Typography>
        ) : (
          <Typography gutterTop='lg'>
            {`You declined your spot in ${apName}'s ${
              application?.program?.name
            } on ${new Date().toLocaleDateString()}. If this was an error, reach out to Guild support to discuss your options. If you are interested in reapplying, please `}
            {supportLink} to submit a new application.
          </Typography>
        )}
      </div>
    </div>
  );
};

export default EnrollmentSuccessModal;
